import { useState, useCallback } from "react";
import { Type, Waves } from "lucide-react";
import { useTextNoiseFill } from "./TextNoiseFillContext";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { CharacterSet } from "./TextNoiseFillContext";
import { PresetSelector } from "../reusable/PresetSelector";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";

const COMMON_FONTS = [
    "monospace",
    "serif",
    "sans-serif",
    "Arial",
    "Times New Roman",
    "Courier New",
    "Georgia",
    "Verdana",
    "Helvetica",
];

const hasLocalFontAccess = "queryLocalFonts" in window;

export function TextNoiseFillControls() {
    const {
        font,
        lineHeight,
        fontSize,
        characterSpacing,
        characterSet,
        noiseScale,
        density,
        minGroupLength,
        seed,
        setFont,
        setLineHeight,
        setFontSize,
        setCharacterSpacing,
        setCharacterSet,
        setNoiseScale,
        setDensity,
        setMinGroupLength,
        setSeed,
        applyPreset,
        getPresetValues,
        resetParameters,
    } = useTextNoiseFill();

    const [systemFonts, setSystemFonts] = useState<string[] | null>(null);
    const [fontAccessError, setFontAccessError] = useState<string | null>(null);

    const loadSystemFonts = useCallback(async () => {
        try {
            const fonts: FontData[] = await (window as any).queryLocalFonts();
            const families = new Set<string>();
            for (const f of fonts) {
                families.add(f.family);
            }
            const sorted = Array.from(families).sort((a, b) =>
                a.localeCompare(b, undefined, { sensitivity: "base" }),
            );
            setSystemFonts(sorted);
            setFontAccessError(null);
        } catch (err) {
            if (err instanceof DOMException && err.name === "NotAllowedError") {
                setFontAccessError("Permission denied.");
            } else {
                setFontAccessError("Failed to load fonts.");
            }
        }
    }, []);

    const fontOptions = systemFonts
        ? [
              ...COMMON_FONTS,
              ...systemFonts.filter((f) => !COMMON_FONTS.includes(f)),
          ]
        : COMMON_FONTS;

    const handleLineHeightChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue <= 0) return;
        setLineHeight(numValue);
    };

    const handleFontSizeChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue <= 0) return;
        setFontSize(numValue);
    };

    const handleCharacterSpacingChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue < 0) return;
        setCharacterSpacing(numValue);
    };

    const handleNoiseScaleChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue <= 0) return;
        setNoiseScale(numValue);
    };

    const handleDensityChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue < 0 || numValue > 1) return;
        setDensity(numValue);
    };

    const handleMinGroupLengthChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue < 1) return;
        setMinGroupLength(numValue);
    };

    return (
        <div className="panel-controls text-noise-fill-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Text Noise Fill</h2>
                    <ToolDescriptionPopover dialogLabel="Text Noise Fill tool description">
                        <p>
                            Fill the canvas with lines of random letters and
                            spaces based on Perlin noise.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                </div>
            </div>
            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="text-noise-fill"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />
                <CollapsibleToolGroup title="Typography" icon={Type}>
                    <div className="field-row">
                        <label className="field-label">
                            Font
                            <select
                                value={font}
                                onChange={(e) => setFont(e.target.value)}
                                className="font-select"
                            >
                                {fontOptions.map((f) => (
                                    <option
                                        key={f}
                                        value={f}
                                        style={{ fontFamily: f }}
                                    >
                                        {f}
                                    </option>
                                ))}
                            </select>
                            <input
                                type="text"
                                value={font}
                                onChange={(e) => setFont(e.target.value)}
                                placeholder="Custom font name"
                                className="font-input"
                            />
                        </label>
                        {hasLocalFontAccess && !systemFonts && (
                            <button
                                type="button"
                                className="generic-button text-noise-fill-load-fonts-btn"
                                onClick={loadSystemFonts}
                            >
                                Load system fonts
                            </button>
                        )}
                        {systemFonts && (
                            <p className="upload-info">
                                {systemFonts.length} system font
                                {systemFonts.length === 1 ? "" : "s"} loaded.
                            </p>
                        )}
                        {fontAccessError && (
                            <p className="upload-error">{fontAccessError}</p>
                        )}
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Line Height (px)
                            <DebouncedInput
                                type="number"
                                min={1}
                                step={1}
                                value={lineHeight}
                                onChange={handleLineHeightChange}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Font Size (px)
                            <DebouncedInput
                                type="number"
                                min={1}
                                step={1}
                                value={fontSize}
                                onChange={handleFontSizeChange}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Character Spacing (px)
                            <DebouncedInput
                                type="number"
                                min={0}
                                step={0.5}
                                value={characterSpacing}
                                onChange={handleCharacterSpacingChange}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Character Set
                            <select
                                value={characterSet}
                                onChange={(e) =>
                                    setCharacterSet(
                                        e.target.value as CharacterSet,
                                    )
                                }
                                className="character-set-select"
                            >
                                <option value="letters">Only Letters</option>
                                <option value="letters-punctuation">
                                    Letters + Punctuation
                                </option>
                                <option value="letters-numbers">
                                    Letters + Numbers
                                </option>
                                <option value="everything">Everything</option>
                                <option value="all-caps-letters">
                                    All Caps Letters
                                </option>
                                <option value="all-caps-letters-numbers">
                                    All Caps Letters + Numbers
                                </option>
                                <option value="all-caps-letters-punctuation">
                                    All Caps Letters + Punctuation
                                </option>
                            </select>
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Noise & fill" icon={Waves}>
                    <div className="field-row">
                        <label className="field-label">
                            Noise Scale
                            <DebouncedInput
                                type="number"
                                min={0.001}
                                step={0.01}
                                value={noiseScale}
                                onChange={handleNoiseScaleChange}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Density (0-1)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={0.01}
                                value={density}
                                onChange={handleDensityChange}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Minimum Group Length
                            <DebouncedInput
                                type="number"
                                min={1}
                                step={1}
                                value={minGroupLength}
                                onChange={handleMinGroupLengthChange}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Seed
                            <input
                                type="text"
                                value={seed}
                                onChange={(e) => setSeed(e.target.value)}
                                placeholder="Enter seed text"
                                className="seed-input"
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>
            </div>
        </div>
    );
}
